import { useEffect } from 'react';
import {
  TPositionMetricEntity,
  TTradingAccountModel,
  useMst,
} from '@trader/store';
import { getAccountTypeForConnection, productId } from '@trader/constants';

import { useGetPageVisibility } from '../useGetPageVisibility';
import { useHub } from './core/useHub';
import {
  EConnectionHub,
  EConnectionHubSubscriber,
  ESocketUpdateAction,
} from './core/types';

interface IMessage {
  id: number;
  symbol: string;
  price: number;
  profit: number;
  stopLoss: number;
  takeProfit: number;
  updateAction: ESocketUpdateAction;
  volume: number;
}

export const usePositions = () => {
  const store = useMst();
  const isVisible = useGetPageVisibility();

  const idToken = store.auth.tokens.idToken || '';

  const activeTradingAccount = store.user.tradingAccounts.find(
    (account: TTradingAccountModel) => account.isInUse
  );
  const login = activeTradingAccount?.platformLogin || '';
  const accountType = activeTradingAccount?.accountType || '';

  const handleUpdate = (message: IMessage) => {
    if (message.updateAction === ESocketUpdateAction.Create) {
      store.entities.positionsMetrics.getPositionsMetricsAsync.run();
    }
    if (message.updateAction === ESocketUpdateAction.Delete) {
      const positionMetric =
        store.entities.positionsMetrics.get<TPositionMetricEntity>(message.id);
      if (!positionMetric?.closePositionAsync.inProgress)
        store.entities.positionsMetrics.delete(message.id);
    }

    store.entities.positionsMetrics.update(message.id, {
      quantity: message.volume,
      price: message.price,
      value: Number((message.price * message.volume).toFixed(1)),
      pl: message.profit,
      takeProfit: message.takeProfit
        ? { type: 'TakeProfit', limitPrice: message.takeProfit }
        : { type: 'TakeProfit', limitPrice: 0 },
      stopLoss: message.stopLoss
        ? { type: 'StopLoss', stopPrice: message.stopLoss }
        : { type: 'StopLoss', stopPrice: 0 },
    });
  };

  const { unsubscribe, subscribe } = useHub<IMessage>({
    url: `${import.meta.env.VITE_SIGNALR_URL}/v2/account`,
    hub: EConnectionHub.Account,
    subscriber: EConnectionHubSubscriber.Positions,
    onMessage: handleUpdate,
    invokedName: 'position',
    subscriptionArgs: {
      login: login,
      productId: productId[import.meta.env.VITE_PRODUCT],
      accountType: getAccountTypeForConnection[accountType],
    },
  });

  useEffect(() => {
    if (login && accountType) {
      subscribe(async connection => {
        await connection?.send(
          'SubscribeOnPositions',
          login || null,
          productId[import.meta.env.VITE_PRODUCT],
          getAccountTypeForConnection[accountType]
        );
      });
    }
    return () => {
      if (login && accountType) {
        unsubscribe(async connection => {
          await connection?.send(
            'UnsubscribeFromPositions',
            login || null,
            productId[import.meta.env.VITE_PRODUCT],
            getAccountTypeForConnection[accountType]
          );
        });
      }
    };
  }, [login, idToken]);

  useEffect(() => {
    if (isVisible) {
      subscribe(async connection => {
        await connection?.send(
          'SubscribeOnPositions',
          login || null,
          productId[import.meta.env.VITE_PRODUCT],
          getAccountTypeForConnection[accountType]
        );
      });
    } else {
      unsubscribe(async connection => {
        await connection?.send(
          'UnsubscribeFromPositions',
          login || null,
          productId[import.meta.env.VITE_PRODUCT],
          getAccountTypeForConnection[accountType]
        );
      });
    }
  }, [isVisible]);
};
